import React from "react"
import { Pressable, StyleSheet, Text, View } from "react-native"
import { theme } from "@common/theme/theme"
import { PopularBadge } from "../../new-subscription/v2/ChangePlanBottomModal"
import { pressOnSpace } from "../utils/pressOnSpace"
import HourBar from "./HourBar"

/**
 * Les formats de cours, tous visibles côte à côte : la barre dit ce que
 * l'élève vit de son heure (avec le masteur, en entraînement), la ligne sous
 * le nom le dit en mots.
 *
 * `tiles` décrit chaque tuile :
 * `{ key, name, share, visits, popular, status, selected, price }`,
 * où `status` vaut `available` ou `locked` (grisée mais touchable : la
 * choisir remet l'engagement).
 *
 * Sans `onOpenInfo`, pas de « i » : la page du format explique le format
 * choisi juste dessous.
 *
 * Le « i » est un voisin de la tuile, pas son enfant : un bouton dans un
 * bouton radio ne se lit ni au clavier ni au lecteur d'écran. « Le plus
 * choisi » est la pastille de l'écran d'abonnement, le jumeau de celui-ci.
 */
const FormatTiles = ({ tiles = [], lockedLabel, onPick, onOpenInfo }) => (
    <View role="radiogroup" aria-label="Format de cours" style={styles.row}>
        {tiles.map((tile) => {
            const locked = tile.status === "locked"
            const press = () => onPick?.(tile.key)
            return (
                <View
                    key={tile.key}
                    style={[styles.cell, tile.popular && styles.cellPopular]}
                >
                    <Pressable
                        role="radio"
                        aria-checked={tile.selected}
                        aria-label={[
                            `Masteur ${tile.name}`,
                            tile.share,
                            tile.price,
                            locked ? lockedLabel : null,
                        ]
                            .filter(Boolean)
                            .join(", ")}
                        onPress={press}
                        {...pressOnSpace(press)}
                        style={[
                            styles.tile,
                            tile.popular && styles.tilePopular,
                            !onOpenInfo && styles.tilePlain,
                            locked && styles.tileMuted,
                            tile.selected && styles.tileSelected,
                        ]}
                    >
                        {locked && lockedLabel ? (
                            <View style={styles.tagWrap} pointerEvents="none">
                                <View style={styles.tagLocked}>
                                    <Text style={styles.tagLockedText}>
                                        {lockedLabel}
                                    </Text>
                                </View>
                            </View>
                        ) : tile.popular ? (
                            <View
                                style={[styles.tagWrap, styles.tagWrapPopular]}
                                pointerEvents="none"
                            >
                                <PopularBadge />
                            </View>
                        ) : null}
                        <HourBar
                            visits={tile.visits}
                            height={14}
                            muted={locked}
                        />
                        <Text style={[styles.brand, locked && styles.muted]}>
                            MASTEUR
                        </Text>
                        <Text style={[styles.name, locked && styles.muted]}>
                            {tile.name}
                        </Text>
                        <Text style={[styles.share, locked && styles.muted]}>
                            {tile.share}
                        </Text>
                        {/* Sans prix (grille non servie, volume pas encore
                            choisi), la ligne garde sa place : les tuiles ne
                            changent pas de hauteur quand il arrive. */}
                        <Text style={[styles.price, locked && styles.muted]}>
                            {tile.price || "\u00a0"}
                        </Text>
                    </Pressable>
                    {onOpenInfo ? (
                        <Pressable
                            role="button"
                            aria-label={`Voir une heure en Masteur ${tile.name}`}
                            onPress={() => onOpenInfo(tile.key)}
                            style={styles.info}
                        >
                            <View style={styles.infoDot}>
                                <Text style={styles.infoText}>i</Text>
                            </View>
                        </Pressable>
                    ) : null}
                </View>
            )
        })}
    </View>
)

const styles = StyleSheet.create({
    // Alignées par le bas : la tuile mise en avant dépasse vers le haut. La
    // marge haute est la place des étiquettes posées à cheval sur le bord.
    row: {
        flexDirection: "row",
        alignItems: "flex-end",
        gap: 8,
        paddingTop: 14,
    },
    cell: { flex: 1, minWidth: 0 },
    cellPopular: { flex: 1.16 },
    tile: {
        alignItems: "center",
        borderWidth: 2,
        borderColor: theme.colors.lightMore,
        borderRadius: 12,
        backgroundColor: theme.colors.white,
        // Sous le « i » : la barre ne passe pas derrière lui.
        paddingTop: 30,
        paddingBottom: 12,
        paddingHorizontal: 10,
    },
    tilePopular: {
        paddingTop: 34,
        paddingBottom: 16,
        shadowColor: theme.colors.primaryDark,
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.1,
        shadowRadius: 14,
        elevation: 3,
    },
    tileMuted: { backgroundColor: theme.colors.whiteAlmost },
    tileSelected: {
        borderColor: theme.colors.primary,
        backgroundColor: theme.colors.primaryLighter,
    },
    // Sans « i » au-dessus de la barre : elle remonte.
    tilePlain: { paddingTop: 22 },
    // Plus large que la tuile : l'étiquette tient sur une ligne même quand
    // elle dépasse des bords.
    tagWrap: {
        position: "absolute",
        top: -11,
        left: -18,
        right: -18,
        alignItems: "center",
    },
    // La pastille partagée est plus haute de 2 px : remontée d'autant, elle
    // garde son bas à 1 px du « i ».
    tagWrapPopular: { top: -13 },
    // « Avec engagement » est long : serré pour tenir dans la largeur d'une
    // tuile latérale à 375 px.
    tagLocked: {
        borderRadius: 999,
        paddingHorizontal: 5,
        paddingVertical: 2,
        backgroundColor: theme.colors.lightMore,
    },
    tagLockedText: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 9,
        color: theme.colors.grey,
    },
    brand: {
        fontFamily: "Montserrat-Bold",
        fontSize: 9.5,
        letterSpacing: 0.8,
        color: theme.colors.greyAlt,
        marginTop: 10,
    },
    // Deux lignes réservées : les tuiles gardent leur hauteur quelle que
    // soit la phrase.
    share: {
        fontFamily: "Montserrat-Medium",
        fontSize: 11,
        lineHeight: 14,
        minHeight: 28,
        textAlign: "center",
        color: theme.colors.grey,
        marginTop: 3,
    },
    name: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 16,
        lineHeight: 19,
        color: theme.colors.primaryDark || theme.colors.primary,
    },
    price: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 13,
        lineHeight: 17,
        color: theme.colors.primary,
        marginTop: 2,
    },
    muted: { color: theme.colors.light },
    // 18 px de pastille dans 30 px de surface : assez pour un pouce.
    info: { position: "absolute", top: 0, right: 0, padding: 6 },
    infoDot: {
        width: 18,
        height: 18,
        borderRadius: 9,
        borderWidth: 1.5,
        borderColor: theme.colors.primary,
        backgroundColor: theme.colors.white,
        alignItems: "center",
        justifyContent: "center",
    },
    infoText: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 11,
        lineHeight: 13,
        color: theme.colors.primary,
    },
})

export default FormatTiles
